import React from 'react';
import { Link } from 'react-router-dom';
import homeImg from '../assets/howToUse/home.png';
import searchPageImg from '../assets/howToUse/search-page.png';
import productPageImg from '../assets/howToUse/product-page.png';
import confirmOrderImg from '../assets/howToUse/confirm-order-page.png';

const WHATSAPP_COMMUNITY_URL = 'https://chat.whatsapp.com/HOwqPNZZATxALHZ2MGN8b8?mode=gi_t';

const processSteps = [
  {
    step: '01',
    title: 'Find What You Want',
    description: 'Search for products using keywords, product photos or a 1688 product link.',
  },
  {
    step: '02',
    title: 'Place Your Order',
    description: 'Review the product and place your order through Checkstall without handling China-side purchasing yourself.',
  },
  {
    step: '03',
    title: 'We Receive and Inspect in China',
    description: 'Your goods arrive at our China warehouse, where they can be checked and prepared for shipment.',
  },
  {
    step: '04',
    title: 'Ship to Nigeria',
    description: 'Choose an available shipping option and have your goods shipped from China to Nigeria.',
  },
];

const benefits = [
  'Source products from Chinese suppliers',
  'Search using 1688 product links',
  'Pay conveniently through Checkstall',
  'RMB trade settlement for supplier payments',
  'China-side receiving and inspection',
  'Shipping support to Nigeria',
  'Optional sourcing assistance when supplier engagement is needed',
];

const appScreens = [
  {
    image: homeImg,
    title: 'Search from your phone',
    alt: 'Checkstall app home screen',
  },
  {
    image: searchPageImg,
    title: 'Find China products',
    alt: 'Checkstall product search screen',
  },
  {
    image: productPageImg,
    title: 'Review product details',
    alt: 'Checkstall product details screen',
  },
  {
    image: confirmOrderImg,
    title: 'Confirm before payment',
    alt: 'Checkstall order confirmation screen',
  },
];

const warehouseVideos = [
  {
    title: 'Warehouse receiving',
    description: 'Goods arriving and being handled by the Checkstall China-side team.',
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/vid-1.mp4',
  },
  {
    title: 'Inspection and packing',
    description: 'A look at goods being checked and prepared before shipment.',
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/vid-2.mp4',
  },
  {
    title: 'Customer goods',
    description: 'Real customer items moving through the warehouse process.',
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/vid-3.mp4',
  },
];

const testimonialScreenshots = [
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-1.jpg',
    alt: 'Customer testimonial screenshot 1',
  },
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-2.jpg',
    alt: 'Customer testimonial screenshot 2',
  },
  {
    src: 'https://checkstall-storage.s3.eu-west-1.amazonaws.com/howItWorks/screenshot-3.jpg',
    alt: 'Customer testimonial screenshot 3',
  },
];

const scrollToProcess = () => {
  document.getElementById('how-checkstall-works')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const HowItWorks = () => {
  return (
    <main className="min-h-screen bg-white text-gray-950">
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-gray-50">
        <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-14 sm:px-6 md:py-20 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="mb-4 inline-flex rounded-full bg-primary-100 px-4 py-2 text-sm font-bold text-primary-700">
              Checkstall China Import
            </p>
            <h1 className="text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
              Import Directly From China With Confidence
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-gray-600 sm:text-xl">
              Source products from Chinese suppliers, pay conveniently, inspect your goods in China and ship to Nigeria - all through Checkstall.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/downloads"
                data-cta-location="hero"
                className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-primary px-7 py-4 text-base font-bold text-white shadow-soft-lg transition-colors hover:bg-primary-600"
              >
                Download Checkstall
              </Link>
              <button
                type="button"
                onClick={scrollToProcess}
                data-track-event="process_cta_click"
                data-cta-location="hero"
                className="inline-flex min-h-14 items-center justify-center rounded-2xl border-2 border-primary bg-white px-7 py-4 text-base font-bold text-primary transition-colors hover:bg-primary-50"
              >
                See How It Works
              </button>
            </div>
            <a
              href={WHATSAPP_COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-cta-location="hero"
              className="mt-4 inline-flex min-h-14 w-full items-center justify-center rounded-2xl bg-green-600 px-7 py-4 text-base font-bold text-white shadow-soft-lg transition-colors hover:bg-green-700 sm:w-auto"
            >
              Join WhatsApp Channel
            </a>
            <p className="mt-3 max-w-xl text-sm leading-6 text-gray-500">
              Not ready to download yet? Join the channel and ask questions before you start importing.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 text-sm text-gray-600 sm:grid-cols-4">
              {['China sourcing', 'Supplier payment', 'Inspection', 'Ship to Nigeria'].map((item) => (
                <div key={item} className="rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-soft">
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="mx-auto w-full max-w-xl">
            <div className="rounded-3xl border border-gray-100 bg-white p-4 shadow-soft-lg">
              <div className="grid grid-cols-2 gap-3">
                {appScreens.map((screen) => (
                  <div key={screen.title} className="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                    <img
                      src={screen.image}
                      alt={screen.alt}
                      className="h-56 w-full object-cover object-top"
                      loading="lazy"
                    />
                    <p className="px-3 py-3 text-sm font-semibold text-gray-800">{screen.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Real operations</p>
              <h2 className="text-3xl font-black sm:text-4xl">See How We Handle Your Goods</h2>
              <p className="mt-4 text-lg leading-8 text-gray-600">
                Watch real warehouse clips showing goods being received, checked and prepared through the Checkstall process.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {warehouseVideos.map((item) => (
                <div key={item.title} className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-soft">
                  <div className="aspect-[9/16] bg-gray-950">
                    <video
                      className="h-full w-full object-cover"
                      controls
                      playsInline
                      preload="metadata"
                      title={item.title}
                    >
                      <source src={item.src} type="video/mp4" />
                    </video>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="how-checkstall-works" className="bg-gray-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Simple process</p>
            <h2 className="text-3xl font-black sm:text-4xl">How Checkstall Works</h2>
            <p className="mt-4 text-lg text-gray-600">
              A short path from product discovery to goods leaving China.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((item) => (
              <div key={item.step} className="rounded-2xl bg-white p-6 shadow-soft">
                <div className="mb-5 text-4xl font-black text-primary">{item.step}</div>
                <h3 className="text-xl font-bold text-gray-950">{item.title}</h3>
                <p className="mt-3 leading-7 text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 rounded-2xl bg-white p-6 text-center shadow-soft">
            <p className="text-lg font-semibold text-gray-900">Need the full step-by-step guide?</p>
            <Link
              to="/how-to-use"
              data-cta-location="process"
              className="mt-4 inline-flex min-h-12 items-center justify-center rounded-2xl border-2 border-primary px-6 py-3 font-bold text-primary transition-colors hover:bg-primary hover:text-white"
            >
              View How to Use Checkstall
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Why Checkstall</p>
              <h2 className="text-3xl font-black sm:text-4xl">Built for China procurement and Nigeria-bound imports</h2>
              <p className="mt-4 text-lg leading-8 text-gray-600">
                Checkstall helps customers reduce the friction of dealing with China-side sourcing, payments, receiving and preparation for shipment.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {benefits.map((benefit) => (
                <div key={benefit} className="flex gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <span className="mt-1 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-primary text-white">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <p className="font-medium text-gray-800">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary">Customer proof</p>
            <h2 className="text-3xl font-black sm:text-4xl">Trusted by Importers</h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Real screenshots from customer conversations and feedback after using Checkstall.
            </p>
          </div>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {testimonialScreenshots.map((item, index) => (
              <div key={item.src} className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-soft">
                <img
                  src={item.src}
                  alt={item.alt}
                  className="h-auto w-full object-contain"
                  loading="lazy"
                />
                <div className="p-4">
                  <p className="text-sm font-semibold text-gray-700">Customer proof #{index + 1}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gray-950 p-6 text-white shadow-soft-lg md:p-10">
            <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-primary-200">WhatsApp community</p>
                <h2 className="text-3xl font-black sm:text-4xl">Still Learning About Importing From China?</h2>
                <p className="mt-4 max-w-3xl text-lg leading-8 text-gray-300">
                  Join our WhatsApp community for importing information, product opportunities, Checkstall updates and guidance on getting started.
                </p>
              </div>
              <a
                href={WHATSAPP_COMMUNITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-cta-location="community"
                className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-7 py-4 text-base font-bold text-gray-950 transition-colors hover:bg-gray-100"
              >
                Join WhatsApp Community
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-primary py-14 text-white md:py-20">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black sm:text-5xl">Ready to Start Importing?</h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-100">
            Download Checkstall Import and start searching for products from China.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/downloads"
              data-cta-location="final"
              className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-white px-7 py-4 font-bold text-primary transition-colors hover:bg-gray-100"
            >
              Download Checkstall Import
            </Link>
            <Link
              to="/how-to-use"
              data-cta-location="final"
              className="inline-flex min-h-14 items-center justify-center rounded-2xl border-2 border-white px-7 py-4 font-bold text-white transition-colors hover:bg-white hover:text-primary"
            >
              View Step-by-Step Tutorial
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HowItWorks;
